import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { User, Calendar, Trash2, ChevronDown, ChevronUp } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MaestroAthleteEnrollments = ({ athlete, onRequestDeletion }) => {
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const fetchEnrollments = async () => {
      try {
        const response = await axios.get(`${API}/maestro/athletes/${athlete.id}/enrollments`);
        setEnrollments(response.data);
      } catch (error) {
        console.error('Error fetching athlete enrollments:', error);
      } finally {
        setLoading(false);
      }
    };

    if (expanded) {
      fetchEnrollments();
    }
  }, [athlete.id, expanded]);

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-100 text-green-800">Approvato</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800">In Attesa</Badge>;
      case 'pending_payment':
        return <Badge className="bg-orange-100 text-orange-800">Attesa Pagamento</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-800">Rifiutato</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <Card className="border-l-4 border-l-red-500">
      <div 
        className="p-4 cursor-pointer hover:bg-gray-50 transition-colors flex items-center justify-between"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center space-x-4">
          <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
            <User className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <p className="font-semibold">{athlete.name} {athlete.surname}</p>
            <p className="text-sm text-gray-500">{athlete.email}</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <Badge variant="outline">{enrollments.length} iscrizioni</Badge>
          {expanded ? (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          )}
        </div>
      </div>

      {expanded && (
        <CardContent className="pt-0 border-t">
          {loading ? (
            <div className="flex justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-600"></div>
            </div>
          ) : enrollments.length === 0 ? (
            <p className="text-gray-500 text-center py-4">
              Nessuna iscrizione per questo atleta.
            </p>
          ) : (
            <div className="space-y-3 mt-4">
              {enrollments.map((enrollment) => (
                <div
                  key={enrollment.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                >
                  <div>
                    <p className="font-medium">{enrollment.event_title}</p>
                    <div className="flex items-center space-x-2 mt-1">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-500">
                        {enrollment.event_start_date && 
                          new Date(enrollment.event_start_date).toLocaleDateString('it-IT')}
                      </span>
                      {getStatusBadge(enrollment.status)}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-red-600 border-red-300 hover:bg-red-50"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRequestDeletion({
                        enrollment_id: enrollment.id,
                        event_title: enrollment.event_title
                      });
                    }}
                    data-testid={`delete-enrollment-${enrollment.id}`}
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Elimina
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};

export default MaestroAthleteEnrollments;
